if (!customElements.get('exit-intent-popup')) {
  const STORAGE_PREFIX = 'cushionday_popup_';
  const EXIT_INTENT_THRESHOLD_PX = 20;
  const COPY_FEEDBACK_MS = 2000;
  const MOBILE_TRIGGER_DELAY_MS = 20000;

  function storageGet(key) {
    try {
      return window.localStorage.getItem(STORAGE_PREFIX + key);
    } catch (e) {
      return null;
    }
  }

  function storageSet(key, value) {
    try {
      window.localStorage.setItem(STORAGE_PREFIX + key, value);
    } catch (e) {
      // localStorage unavailable (private mode, disabled, quota) - fail silently,
      // the popup simply won't persist state across page loads.
    }
  }

  function generateVisitorId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    // Fallback UUID v4 for browsers without crypto.randomUUID (e.g. Safari < 15.4).
    const bytes = new Uint8Array(16);
    if (window.crypto && typeof window.crypto.getRandomValues === 'function') {
      window.crypto.getRandomValues(bytes);
    } else {
      for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
    }
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
    return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex
      .slice(8, 10)
      .join('')}-${hex.slice(10, 16).join('')}`;
  }

  function getOrCreateVisitorId() {
    let visitorId = storageGet('visitorId');
    if (!visitorId) {
      visitorId = generateVisitorId();
      storageSet('visitorId', visitorId);
    }
    return visitorId;
  }

  function readState() {
    return {
      visitorId: getOrCreateVisitorId(),
      claimStatus: storageGet('claimStatus') || 'none',
      code: storageGet('code'),
      expiresAt: storageGet('expiresAt'),
    };
  }

  /**
   * PRODUCTION WIRING - calls the AWS Lambda Function URL directly (no
   * Shopify app proxy involved). Paste the real ClaimDiscountUrl value
   * printed by `sam deploy`'s Outputs section in place of the placeholder
   * below before this goes live.
   */
  const CLAIM_DISCOUNT_URL = 'https://yaeevntuyljvxfubtnudab5jjq0autzg.lambda-url.ap-south-1.on.aws/';

  async function claimDiscount(payload) {
    const response = await fetch(CLAIM_DISCOUNT_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error('claim_failed');
    return response.json(); // { code, expiresAt }
  }

  function isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  customElements.define(
    'exit-intent-popup',
    class ExitIntentPopup extends ModalDialog {
      constructor() {
        super();

        this.overlay = this.querySelector('.exit-intent-popup__overlay');
        this.form = this.querySelector('#ExitIntentPopupForm');
        this.formError = this.querySelector('#ExitIntentPopupForm-error');
        this.nameInput = this.querySelector('#ExitIntentPopup-name');
        this.nameError = this.querySelector('#ExitIntentPopup-name-error');
        this.emailInput = this.querySelector('#ExitIntentPopup-email');
        this.emailError = this.querySelector('#ExitIntentPopup-email-error');
        this.submitButton = this.querySelector('.exit-intent-popup__submit');
        this.dismissButton = this.querySelector('.exit-intent-popup__dismiss');
        this.successPanel = this.querySelector('#ExitIntentPopupSuccess');
        this.codeElement = this.querySelector('[data-discount-code]');
        this.copyButton = this.querySelector('[data-copy-button]');
        this.copyLabel = this.querySelector('[data-copy-label]');
        this.copyFeedback = this.querySelector('[data-copy-feedback]');
        this.countdownHours = this.querySelector('[data-countdown-hours]');
        this.countdownMinutes = this.querySelector('[data-countdown-minutes]');
        this.countdownSeconds = this.querySelector('[data-countdown-seconds]');

        this.strings = {
          copyButton: this.copyLabel ? this.copyLabel.textContent : 'Copy Code',
          copiedLabel: window.exitIntentPopupStrings ? window.exitIntentPopupStrings.copiedLabel : 'Copied!',
          errorNameRequired: window.exitIntentPopupStrings
            ? window.exitIntentPopupStrings.errorNameRequired
            : 'Please enter your first name.',
          errorEmailRequired: window.exitIntentPopupStrings
            ? window.exitIntentPopupStrings.errorEmailRequired
            : 'Please enter your email address.',
          errorEmailInvalid: window.exitIntentPopupStrings
            ? window.exitIntentPopupStrings.errorEmailInvalid
            : 'Please enter a valid email address.',
          errorGeneric: window.exitIntentPopupStrings
            ? window.exitIntentPopupStrings.errorGeneric
            : 'Something went wrong. Please try again.',
        };

        this.countdownInterval = null;
        this.triggerFired = false;
        this.mobileTriggerTimeout = null;
        this.onMouseMove = this.onMouseMove.bind(this);
        this.onMouseOut = this.onMouseOut.bind(this);
        this.lastY = null;

        if (this.overlay) this.overlay.addEventListener('click', () => this.hide());
        if (this.dismissButton) this.dismissButton.addEventListener('click', () => this.onDismiss());
        if (this.form) this.form.addEventListener('submit', (event) => this.onSubmit(event));
        if (this.copyButton) this.copyButton.addEventListener('click', () => this.onCopyCode());
      }

      connectedCallback() {
        super.connectedCallback();
        this.init();
      }

      init() {
        const state = readState();

        if (state.claimStatus === 'dismissed') {
          return;
        }

        if (state.claimStatus === 'claimed') {
          const stillValid = state.expiresAt && Date.parse(state.expiresAt) > Date.now();
          if (!stillValid) {
            // Claimed but the code has expired - respect the "already interacted" rule, never re-attach.
            return;
          }
          // Claimed and still valid: don't attach the exit-intent trigger again (no need to
          // nag a visitor who already has a code). show() still guards this state defensively
          // in case anything else ever calls it directly.
          return;
        }

        this.attachTrigger();
      }

      attachTrigger() {
        // Exit-intent (mouseout near the top edge) has no equivalent on touch devices -
        // there's no cursor to leave through the top of the viewport. Below the theme's
        // own mobile breakpoint, fall back to a fixed delay instead.
        if (this.isMobileViewport()) {
          this.mobileTriggerTimeout = window.setTimeout(() => {
            if (this.triggerFired || this.isOtherPopupOpen()) return;
            this.triggerFired = true;
            this.mobileTriggerTimeout = null;
            this.openFromTrigger();
          }, MOBILE_TRIGGER_DELAY_MS);
          return;
        }

        // mousemove only tracks position for the upward-motion check below - it does NOT
        // fire the popup. mousemove stops dispatching the moment the cursor leaves the
        // document (into the tab bar, URL bar, window controls), so by the time someone is
        // heading for the close button there are no more events to test against a threshold.
        document.addEventListener('mousemove', this.onMouseMove);
        // mouseout + relatedTarget is what actually confirms the cursor exited the viewport
        // (relatedTarget is null, or the <html> element, only when it leaves the window -
        // moving onto any other in-page element gives a real relatedTarget instead).
        document.addEventListener('mouseout', this.onMouseOut);
      }

      detachTrigger() {
        document.removeEventListener('mousemove', this.onMouseMove);
        document.removeEventListener('mouseout', this.onMouseOut);
        if (this.mobileTriggerTimeout) {
          window.clearTimeout(this.mobileTriggerTimeout);
          this.mobileTriggerTimeout = null;
        }
      }

      isMobileViewport() {
        // Matches this component's own CSS breakpoint (see exit-intent-popup.css).
        return window.matchMedia('(max-width: 749px)').matches;
      }

      onMouseMove(event) {
        this.lastY = event.clientY;
      }

      onMouseOut(event) {
        if (this.triggerFired) return;

        const exitedViewport = event.relatedTarget === null || event.relatedTarget.nodeName === 'HTML';
        const nearTopEdge = event.clientY <= EXIT_INTENT_THRESHOLD_PX;
        const movingUp = this.lastY !== null && event.clientY <= this.lastY;

        if (exitedViewport && nearTopEdge && movingUp) {
          // fomo-popup is independent (either may show in the same session), but skip firing
          // while it's still open so the two full-screen modals don't stack - the listener
          // stays attached, so this retries on the next qualifying mouseout.
          if (this.isOtherPopupOpen()) return;
          this.triggerFired = true;
          this.detachTrigger();
          this.openFromTrigger();
        }
      }

      isOtherPopupOpen() {
        const fomoPopup = document.querySelector('fomo-popup');
        return !!(fomoPopup && fomoPopup.hasAttribute('open'));
      }

      openFromTrigger() {
        this.show();
      }

      show(opener) {
        const state = readState();

        if (state.claimStatus === 'claimed' && state.expiresAt && Date.parse(state.expiresAt) > Date.now()) {
          this.renderSuccess(state.code);
        }

        super.show(opener);
        this.startCountdown();
        this.suspendBackgroundVideos();
      }

      hide() {
        this.stopCountdown();
        this.resumeBackgroundVideos();
        super.hide();
      }

      // Autoplaying background video (e.g. the homepage collection-grid tiles) paints through
      // its own internal media-presentation layer, which bypasses normal compositing - so
      // backdrop-filter can't sample it and the overlay blur silently no-ops over any area a
      // video occupies. This holds even once the video is paused/unloaded down to just its
      // <poster> frame, since that poster still renders via the same video element. The only
      // reliable fix is to swap the video out for a plain <img> of its poster while the popup is
      // open (a normal <img> composites the regular way, which the blur can see fine) and swap
      // it back on close.
      suspendBackgroundVideos() {
        this.suspendedVideos = [];
        document.querySelectorAll('video[autoplay]').forEach((video) => {
          if (this.contains(video) || !video.poster) return;

          video.pause();
          const standIn = document.createElement('img');
          standIn.src = video.poster;
          standIn.className = video.className;
          standIn.setAttribute('aria-hidden', 'true');
          video.insertAdjacentElement('afterend', standIn);

          const previousDisplay = video.style.display;
          video.style.display = 'none';
          this.suspendedVideos.push({ video, standIn, previousDisplay });
        });
      }

      resumeBackgroundVideos() {
        (this.suspendedVideos || []).forEach(({ video, standIn, previousDisplay }) => {
          standIn.remove();
          video.style.display = previousDisplay;
          video.play().catch(() => {});
        });
        this.suspendedVideos = [];
      }

      startCountdown() {
        this.stopCountdown();

        // The timer only starts once a code actually exists (right after claimDiscount
        // resolves), so the visitor sees a live "you have 24 hours" clock, not a countdown
        // ticking before there's anything to redeem. Before that, the blocks stay at the
        // static placeholder value already in the markup (24 / 00 / 00).
        const state = readState();
        if (state.claimStatus !== 'claimed' || !state.expiresAt) return;

        this.updateCountdown();
        this.countdownInterval = window.setInterval(() => this.updateCountdown(), 1000);
      }

      stopCountdown() {
        if (this.countdownInterval) {
          window.clearInterval(this.countdownInterval);
          this.countdownInterval = null;
        }
      }

      updateCountdown() {
        const expiresAt = storageGet('expiresAt');
        const remainingMs = Math.max(0, (expiresAt ? Date.parse(expiresAt) : Date.now()) - Date.now());

        const totalSeconds = Math.floor(remainingMs / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        if (this.countdownHours) this.countdownHours.textContent = String(hours).padStart(2, '0');
        if (this.countdownMinutes) this.countdownMinutes.textContent = String(minutes).padStart(2, '0');
        if (this.countdownSeconds) this.countdownSeconds.textContent = String(seconds).padStart(2, '0');

        if (remainingMs <= 0) {
          this.stopCountdown();
        }
      }

      validate() {
        let valid = true;

        const name = this.nameInput.value.trim();
        if (!name) {
          this.showFieldError(this.nameInput, this.nameError, this.strings.errorNameRequired);
          valid = false;
        } else {
          this.clearFieldError(this.nameInput, this.nameError);
        }

        const email = this.emailInput.value.trim();
        if (!email) {
          this.showFieldError(this.emailInput, this.emailError, this.strings.errorEmailRequired);
          valid = false;
        } else if (!isValidEmail(email)) {
          this.showFieldError(this.emailInput, this.emailError, this.strings.errorEmailInvalid);
          valid = false;
        } else {
          this.clearFieldError(this.emailInput, this.emailError);
        }

        return valid;
      }

      showFieldError(input, errorElement, message) {
        input.setAttribute('aria-invalid', 'true');
        if (errorElement) {
          const messageEl = errorElement.querySelector('[data-name-error-message], [data-email-error-message]');
          if (messageEl) messageEl.textContent = message;
          errorElement.classList.remove('hidden');
        }
      }

      clearFieldError(input, errorElement) {
        input.removeAttribute('aria-invalid');
        if (errorElement) errorElement.classList.add('hidden');
      }

      showFormError(message) {
        if (!this.formError) return;
        const messageEl = this.formError.querySelector('[data-form-error-message]');
        if (messageEl) messageEl.textContent = message;
        this.formError.classList.remove('hidden');
      }

      clearFormError() {
        if (this.formError) this.formError.classList.add('hidden');
      }

      async onSubmit(event) {
        event.preventDefault();
        this.clearFormError();

        if (!this.validate()) return;

        const state = readState();
        this.submitButton.disabled = true;
        this.submitButton.setAttribute('aria-busy', 'true');

        try {
          const result = await claimDiscount({
            visitorId: state.visitorId,
            name: this.nameInput.value.trim(),
            email: this.emailInput.value.trim(),
          });

          storageSet('claimStatus', 'claimed');
          storageSet('code', result.code);
          storageSet('expiresAt', result.expiresAt);

          this.renderSuccess(result.code);
          this.startCountdown();
        } catch (error) {
          this.showFormError(this.strings.errorGeneric);
        } finally {
          this.submitButton.disabled = false;
          this.submitButton.removeAttribute('aria-busy');
        }
      }

      renderSuccess(code) {
        if (this.codeElement) this.codeElement.textContent = code;
        // .hidden (class), not the hidden attribute: .exit-intent-popup__form and
        // .exit-intent-popup__success both set an explicit `display` value, which beats
        // the [hidden] UA-default display:none in the cascade - same reason this theme's
        // own .form__message error boxes use the .hidden utility class instead of the
        // attribute (see base.css .hidden { display: none !important }).
        if (this.form) this.form.classList.add('hidden');
        if (this.successPanel) this.successPanel.classList.remove('hidden');
        // Figma's success state has no dismiss control at all (ESC / overlay click still work).
        if (this.dismissButton) this.dismissButton.classList.add('hidden');
      }

      onCopyCode() {
        const code = this.codeElement ? this.codeElement.textContent : '';
        if (!code) return;

        const revert = () => {
          if (this.copyLabel) this.copyLabel.textContent = this.strings.copyButton;
          if (this.copyButton) this.copyButton.removeAttribute('data-copied');
        };

        const onCopied = () => {
          if (this.copyLabel) this.copyLabel.textContent = this.strings.copiedLabel;
          if (this.copyButton) this.copyButton.setAttribute('data-copied', '');
          if (this.copyFeedback) this.copyFeedback.textContent = this.strings.copiedLabel;
          window.setTimeout(revert, COPY_FEEDBACK_MS);
        };

        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(code).then(onCopied).catch(() => this.copyFallback(code, onCopied));
        } else {
          this.copyFallback(code, onCopied);
        }
      }

      copyFallback(code, onCopied) {
        const textarea = document.createElement('textarea');
        textarea.value = code;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        try {
          document.execCommand('copy');
          onCopied();
        } catch (e) {
          // Clipboard unavailable - the code is still visible on screen for manual copy.
        } finally {
          document.body.removeChild(textarea);
        }
      }

      onDismiss() {
        storageSet('claimStatus', 'dismissed');
        this.detachTrigger();
        this.hide();
      }
    }
  );
}